import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

/**
 * Корни очередей на экране: переписка → смонтированные корни `PendingMessages` («Чаты» и модалка
 * брони могут показывать одну переписку разом). По клиенту запросов, а не модулю: у каждого клиента
 * свои попытки отправки, и корни одного не видны другому.
 */
const roots = new WeakMap<QueryClient, Map<string, Set<Element>>>()

/** Предки, обрезающие содержимое по своей коробке. */
const CLIPS = new Set(['auto', 'scroll', 'hidden', 'clip'])

/**
 * Отказ виден: сообщение в DOM, не под модалкой (она прячет остальное `aria-hidden`), и его строка
 * отказа под пузырём — хоть краем в окне каждой своей прокрутки. Видный краем пузырь без строки
 * неотличим от ушедшего, а под модалкой брони или ниже края ленты, пролистанной вверх, отказ не
 * увидит никто.
 */
export function inView(item: Element): boolean {
  if (!item.isConnected || item.closest('[aria-hidden="true"], [inert]')) return false
  let top = 0
  let bottom = window.innerHeight
  for (let parent = item.parentElement; parent; parent = parent.parentElement) {
    if (!CLIPS.has(getComputedStyle(parent).overflowY)) continue
    const port = parent.getBoundingClientRect()
    top = Math.max(top, port.top)
    bottom = Math.min(bottom, port.bottom)
  }
  // Сам элемент сообщения — `display: contents`: меряем строку отказа, её помечает `MessageBubble`.
  // Строки нет — сообщение не упало, и видеть нечего.
  const box = item.querySelector('[data-failed-line]')?.getBoundingClientRect()
  return Boolean(box && box.height > 0 && box.bottom > top && box.top < bottom)
}

/** Видит ли человек отказ этого сообщения — тогда говорит его строка, а не тост. */
export function isOutboxShown(
  queryClient: QueryClient,
  conversationId: string,
  idempotencyKey: string,
): boolean {
  for (const root of roots.get(queryClient)?.get(conversationId) ?? []) {
    const item = root.querySelector(`[data-unsent="${idempotencyKey}"]`)
    if (item && inView(item)) return true
  }
  return false
}

/** Ref корня очереди: сообщения внутри помечены `data-unsent` своим ключом идемпотентности. */
export function useOutboxRoot(conversationId: string): (root: Element | null) => (() => void) | undefined {
  const queryClient = useQueryClient()

  return useCallback(
    (root: Element | null) => {
      if (!root) return undefined
      const byConversation = roots.get(queryClient) ?? new Map<string, Set<Element>>()
      roots.set(queryClient, byConversation)
      const shown = byConversation.get(conversationId) ?? new Set<Element>()
      byConversation.set(conversationId, shown)
      shown.add(root)
      return () => {
        shown.delete(root)
      }
    },
    [queryClient, conversationId],
  )
}
